/**
 * API-Client: same-origin, Session-Cookie, CSRF-Header auf Mutationen.
 * Fehler tragen einen Code; die Übersetzung übernimmt die i18n-Schicht.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly fields?: { path: string; message: string }[],
    readonly payload?: Record<string, unknown>,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

type Json = Record<string, unknown> | unknown[] | null;

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function req<T = any>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        'x-werkdelta-csrf': '1',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('OFFLINE', 0);
  }
  const data: Json = await res.json().catch(() => null);
  if (!res.ok) {
    const record = (data ?? {}) as Record<string, unknown>;
    const code = typeof record.code === 'string' ? record.code : 'INTERNAL';
    if (res.status === 401 && code !== 'LOGIN_FAILED' && code !== 'MFA_CODE_INVALID') {
      onUnauthorized?.();
    }
    throw new ApiError(
      code,
      res.status,
      record.fields as { path: string; message: string }[] | undefined,
      record,
    );
  }
  return data as T;
}

export const api = {
  get: <T = any>(url: string) => req<T>('GET', url),
  post: <T = any>(url: string, body?: unknown) => req<T>('POST', url, body),
  patch: <T = any>(url: string, body?: unknown) => req<T>('PATCH', url, body),
  put: <T = any>(url: string, body?: unknown) => req<T>('PUT', url, body),
  del: <T = any>(url: string, body?: unknown) => req<T>('DELETE', url, body),
  async upload<T = any>(url: string, file: File): Promise<T> {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'x-werkdelta-csrf': '1' },
      body: form,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const record = (data ?? {}) as Record<string, unknown>;
      throw new ApiError(
        typeof record.code === 'string' ? (record.code as string) : 'INTERNAL',
        res.status,
        undefined,
        record,
      );
    }
    return data as T;
  },
};
